import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { whenAvailable } from '@vp/ab-reader';
import { addAdditionalAttributes } from '@vp/om-newrelic-tracker';
import {
  buildActiveExperiments,
  buildAvailableExperiments,
  getActiveFeatureGates,
} from '../../utils/experimentsUtils';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
  supportedExperiments,
  supportedFeatureGates,
} from '../../constants/experiments';
import type {
  Experiment,
  GenericExperiment,
  OverrideExperiment,
} from '../../types/experiment.types';
import { NEWRELIC_BROWSER_AGENT_IDENTIFIER } from '../../constants/newRelic';
import { UserContextContext } from '@vp/ubik-context';
import type { ExperimentsContextType } from '../../types/experimentContext.types';

/**
 * This context provides a wrapper that manages the experiments for the application.
 * The expected structure of a single activeExperiment is:
 {
    experimentKey: string,  // the unique experimentKey identifier.
    variationKey: string,  // optional, the variationKey selected for the experiment.
    availableVariationKeys: [string], // the available variationKeys for the experiment.
    shouldFireImpression: bool // whether or not an impression should be fired when using this experiment.
                               // Notably, experiments that are not enabled for the site, not present for the current user, or have been modified via debug overrides will be false.
  }
 */
const ExperimentsContext = createContext<ExperimentsContextType | null>(null);

const ExperimentsProvider = ({ children }: { children: ReactNode }) => {
  const userContext = useContext(UserContextContext);
  let ubikExperiments: { [key: string]: string | undefined } | undefined =
    undefined;
  let ubikFeatureGates: { [key: string]: string | undefined } | undefined =
    undefined;

  if (userContext) {
    ({
      userContext: {
        experiments: ubikExperiments,
        featureFlags: ubikFeatureGates,
      },
    } = userContext);
  }
  // is the ab-reader initialized and available
  const [isReaderInitialized, setIsReaderInitialized] =
    useState<boolean>(false);

  // Array of experiment objects for the given user as determined by @vp/ab-reader.
  const [assignedExperiments, setAssignedExperiments] = useState<
    GenericExperiment[]
  >([]);

  // Array of experiments that are available for the application as determined by the supportedExperiments, and assignedExperiments.
  // See buildAvailableExperiments for more details.
  const [availableExperiments, setAvailableExperiments] = useState<
    Experiment[]
  >([]);

  // Array of experiments that can override an experiment in availableExperiments.
  const [debugExperimentOverrides, setDebugExperimentOverrides] = useState<
    OverrideExperiment[]
  >([]);

  // Array of experiments exposed to the rest of the application. Contains the result of applying the debug overrides to the available experiments.
  const [activeExperiments, setActiveExperiments] = useState<Experiment[]>([]);

  // Array of active feature gates
  const [activeFeatureGates, setActiveFeatureGates] = useState<string[]>([]);

  // Array of feature gates overrides.
  const [debugFeatureGateOverrides, setDebugFeatureGateOverrides] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      whenAvailable(async (available) => {
        // defaulted to 1000ms per ab-reader README example: https://gitlab.com/vistaprint-org/merchandising-technology/experimentation-platform/ab-libraries/ab-reader-js/-/tree/main
        if (available) {
          setIsReaderInitialized(true);
          setAssignedExperiments(
            // need to make the shape of ubikExperiments consistent with ab-reader
            Object.keys(ubikExperiments || {}).reduce(
              (acc: GenericExperiment[], currentValue: string) => {
                if (ubikExperiments) {
                  return [
                    ...acc,
                    {
                      experimentKey: currentValue,
                      variationKey: ubikExperiments[currentValue],
                    },
                  ];
                }
                return acc;
              },
              []
            )
          );
        }
      }, 1000);
    }
  }, [ubikExperiments]);

  useEffect(() => {
    setAvailableExperiments(
      buildAvailableExperiments({
        supportedExperiments,
        assignedExperiments,
      })
    );
  }, [assignedExperiments]);

  useEffect(() => {
    // filter out experiments that may be active but in which the user is not currently participating
    const currentExperiments = activeExperiments?.filter(
      (exp) => exp?.['variationKey'] !== undefined
    );
    if (currentExperiments?.length > 0) {
      const activeExperimentsString = currentExperiments
        ?.map((exp) => `${exp?.['experimentKey']}:${exp?.['variationKey']}`)
        .join(',');
      // track experiments in new relic
      addAdditionalAttributes(NEWRELIC_BROWSER_AGENT_IDENTIFIER, {
        activeExperiments: activeExperimentsString,
      });
    }
  }, [activeExperiments]);

  const overrideExperiment = useCallback(
    ({
      experimentKey,
      variationKey,
      availableVariationKeys,
    }: {
      experimentKey: string;
      variationKey: string;
      availableVariationKeys: string[];
    }) => {
      const debugExperimentOverridesClone = [...debugExperimentOverrides];
      const overrideExperimentIndex = debugExperimentOverridesClone.findIndex(
        (experiment) => experiment?.['experimentKey'] === experimentKey
      );

      // feature override already exists, set the variation key and fireImpression state
      if (
        overrideExperimentIndex > -1 &&
        debugExperimentOverridesClone[overrideExperimentIndex]
      ) {
        debugExperimentOverridesClone[overrideExperimentIndex] = {
          ...debugExperimentOverridesClone[overrideExperimentIndex],
          variationKey,
          shouldFireImpression: false,
        };
        setDebugExperimentOverrides(debugExperimentOverridesClone);
        return;
      }

      // feature override does not exist
      debugExperimentOverridesClone.push({
        experimentKey,
        variationKey,
        availableVariationKeys,
        shouldFireImpression: false,
      });
      setDebugExperimentOverrides(debugExperimentOverridesClone);
    },
    [debugExperimentOverrides]
  );

  useDeepCompareEffect(
    () =>
      setActiveExperiments(
        buildActiveExperiments({
          availableExperiments,
          debugExperimentOverrides,
        })
      ),
    [availableExperiments, debugExperimentOverrides]
  );

  useDeepCompareEffect(() => {
    if (isReaderInitialized && ubikFeatureGates) {
      setActiveFeatureGates(
        getActiveFeatureGates({
          ubikFeatureGates,
          supportedFeatureGates,
          debugFeatureGateOverrides,
        })
      );
    }
  }, [debugFeatureGateOverrides, isReaderInitialized]);

  const isFeatureEnabled = useCallback(
    (feature: string) => activeFeatureGates.includes(feature),

    [activeFeatureGates]
  );

  const experimentsContextValues = useMemo(
    () => ({
      overrideExperiment,
      isFeatureEnabled,
      activeExperiments,
      setDebugFeatureGateOverrides,
      supportedExperiments,
      supportedFeatureGates,
    }),
    [activeExperiments, isFeatureEnabled, overrideExperiment]
  );

  return (
    <ExperimentsContext.Provider value={experimentsContextValues}>
      {children}
    </ExperimentsContext.Provider>
  );
};

const useExperimentsContext = () => {
  const context = useContext(ExperimentsContext);
  if (!context) {
    throw new Error(
      'useExperimentsContext must be used within an ExperimentsProvider'
    );
  }
  return context;
};

export { ExperimentsContext, ExperimentsProvider, useExperimentsContext };
